// Copyright (c) Docugami, Inc. All rights reserved.

import { createSelector } from "@reduxjs/toolkit";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { ChunkInstance, UserFeedbackActions } from "networking/api";
import { AppState } from "store";
import { selectIsDgmlInitialized, dgmlNodesSelector } from "./dgml/dgmlSelectors";

export const selectSelectionDefaultLabel = (state: AppState) =>
    state.sdv.document?.selection?.defaultLabel;

export const selectSelectionModifier = (state: AppState) =>
    state.sdv.document?.selection?.selectionModifier;

export const selectEditTokenRange = (state: AppState) =>
    state.sdv.document?.edit?.tokenRange;

export const selectSelectionId = (state: AppState) =>
    state.sdv.document?.selection?.id;

export const selectIsEditingSelection = (state: AppState) => !!state.sdv.document?.edit;

export const selectSelectedChunkInstance = createSelector(
    [
        (state: AppState) => state.sdv.document?.selection?.id,
        (state: AppState) => state.sdv.document?.folderId,
        (state: AppState) => {
            const document = state.sdv.document;
            if (!!document) {
                return selectIsDgmlInitialized(state, document.docsetId, document.fileId)
            }

            return false;
        },
        (state: AppState) => {
            const document = state.sdv.document;
            if (!!document && !!document.selection?.id) {
                return dgmlNodesSelector(document.docsetId, document.fileId)(state).data?.chunks.entities[document.selection.id];
            }
        }
    ],
    (selectedId, folderId) => {
        const result = !!folderId && !!selectedId ? (DgmlInstancesSingleton.getInstanceById(folderId, selectedId) ?? undefined) : undefined
        return result;
    }
)

export const selectEditedChunkInstance = createSelector(
    [
        selectSelectedChunkInstance,
        (state: AppState) => state.sdv.document?.folderId,
        (state: AppState) => state.sdv.document?.edit
    ],
    (selectedInstance, folderId, editOperation): ChunkInstance | undefined => {
        if (!editOperation || !folderId) {
            return;
        }

        if (!selectedInstance) {
            // When we have an edit operation w/o a selected chunk, that means the user is creating a new one so we create a placeholder chunk
            return {
                folderId: folderId,
                spanId: DgmlInstancesSingleton.getSpanIdForTokenRange(folderId, editOperation.tokenRange),
                userActions: UserFeedbackActions.Created,
                tokenRange: editOperation.tokenRange
            }
        }
        else {
            return {
                ...selectedInstance,
                spanId: DgmlInstancesSingleton.getSpanIdForTokenRange(folderId, editOperation.tokenRange),
                tokenRange: editOperation.tokenRange
            }
        }
    }
)

export const selectCurrentChunkInstance = createSelector(
    [
        selectSelectedChunkInstance,
        selectEditedChunkInstance
    ],
    (selectedChunkInstance, editedChunkInstance): ChunkInstance | undefined => {
        return editedChunkInstance || selectedChunkInstance;
    }
)